import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import {
  getProductImageValidationSettings,
  listProductImageValidationRuns,
  saveProductImageValidationSettings,
} from '../../modules/productImageValidation/service';
import {
  ProductImageValidationCategoryRoot,
  ProductImageValidationRunSummary,
  ProductImageValidationSettings,
} from '../../modules/productImageValidation/types';
import {
  useProductImageValidationRunner,
} from '../../modules/productImageValidation/useProductImageValidationRunner';
import ProductImageValidationRunDialog from '../../modules/productImageValidation/ProductImageValidationRunDialog';

const RULES = [
  { label: 'Filformat', description: 'Bilden måste vara i JPG/JPEG-format' },
  { label: 'Färgrymd', description: 'Bilden måste ha RGB-färgrymd. Profilnamn visas som information när det finns.' },
  { label: 'Upplösning', description: '300 DPI' },
  { label: 'Pixlar', description: 'Minst en sida måste nå konfigurerat pixelkrav' },
  { label: 'Urklippsbana', description: 'Måste ha en urklippsbana med namnet "Path 1"' },
];

function statusChip(run: ProductImageValidationRunSummary) {
  const allPassed = run.failedCount === 0 && run.errorCount === 0;
  if (allPassed) {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label="Alla godkända"
        size="small"
        sx={{ backgroundColor: '#c8e6c9', fontWeight: 600, fontSize: '0.75rem' }}
      />
    );
  }
  return (
    <Chip
      icon={<CancelIcon />}
      label={`${run.failedCount + run.errorCount} underkända`}
      size="small"
      sx={{ backgroundColor: '#ffcdd2', fontWeight: 600, fontSize: '0.75rem' }}
    />
  );
}

const ProductImageValidationPage: React.FC = () => {
  const [runs, setRuns] = useState<ProductImageValidationRunSummary[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [settings, setSettings] = useState<ProductImageValidationSettings>({ minRequiredSidePx: 2500 });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadRuns = useCallback(async () => {
    try {
      setLoadingRuns(true);
      setRunsError(null);
      const nextRuns = await listProductImageValidationRuns(25);
      setRuns(nextRuns);
    } catch (err: any) {
      setRunsError(err?.response?.data?.message || err?.message || 'Kunde inte ladda tidigare körningar.');
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  const runner = useProductImageValidationRunner({
    onRunFinished: () => {
      void loadRuns();
    },
  });

  const loadSettings = useCallback(async () => {
    try {
      setLoadingSettings(true);
      const nextSettings = await getProductImageValidationSettings();
      setSettings(nextSettings);
    } catch (err: any) {
      setSettingsMessage({
        type: 'error',
        text: err?.response?.data?.message || err?.message || 'Kunde inte ladda inställningar.',
      });
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  useEffect(() => {
    void loadRuns();
    void loadSettings();
  }, [loadRuns, loadSettings]);

  const handleRunCategory = async (categoryRoot: ProductImageValidationCategoryRoot) => {
    await runner.startRun('all', undefined, categoryRoot);
  };

  const runScopeLabel = (run: ProductImageValidationRunSummary) => {
    if (run.scope !== 'all') return 'Valda bilder';
    return run.categoryRoot === 'vin' ? 'Alla vinbilder' : 'Alla produktbilder';
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      setSettingsMessage(null);
      const nextSettings = await saveProductImageValidationSettings(settings);
      setSettings(nextSettings);
      setSettingsMessage({
        type: 'success',
        text: 'Inställningen är sparad. Samma pixelkrav används i Validoo-preflighten.',
      });
    } catch (err: any) {
      setSettingsMessage({
        type: 'error',
        text: err?.response?.data?.message || err?.message || 'Kunde inte spara inställningar.',
      });
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700, color: '#072f53' }}>
        Bildkontroll Validoo
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        Innan bilder kan exporteras till Validoo måste de uppfylla ett antal tekniska krav.
        Välj om kontrollen ska köras för alla bilder under Produkter eller alla bilder under Vin.
        Samma regler gäller båda kategorierna: filformat (JPG), färgrymd (RGB), upplösning (300 DPI),
        bildstorlek och urklippsbana ("Path 1"). Resultaten hjälper dig att identifiera och åtgärda
        bilder innan export, och samma regler används automatiskt i Validoo-preflighten.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Kontrollregler</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {RULES.map((rule) => (
            <Tooltip key={rule.label} title={rule.description} arrow placement="top">
              <Chip
                icon={<CheckCircleIcon />}
                label={rule.label}
                size="small"
                variant="outlined"
                sx={{ cursor: 'help' }}
              />
            </Tooltip>
          ))}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Pixelkrav</Typography>
        {loadingSettings ? (
          <CircularProgress size={24} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                type="number"
                label="Minst en sida (px)"
                value={settings.minRequiredSidePx}
                onChange={(event) => setSettings((prev) => ({
                  ...prev,
                  minRequiredSidePx: Math.max(1, Number(event.target.value) || 1),
                }))}
                size="small"
                sx={{ width: 200 }}
                inputProps={{ min: 1, step: 1 }}
              />
              <Button
                variant="contained"
                startIcon={savingSettings ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
                onClick={handleSaveSettings}
                disabled={savingSettings}
                size="small"
                sx={{ backgroundColor: '#072f53', '&:hover': { backgroundColor: '#0a3d6b' } }}
              >
                Spara
              </Button>
            </Box>
            {settingsMessage && (
              <Alert severity={settingsMessage.type} onClose={() => setSettingsMessage(null)} sx={{ py: 0 }}>
                {settingsMessage.text}
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary">
              Minst en sida på bilden måste vara detta antal pixlar eller fler. Samma värde används i Validoo-preflighten.
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Kör kontroll</Typography>
            <Typography variant="body2" color="text.secondary">
              Kontrollera alla bilder under Produkter eller Vin mot reglerna ovan. Körningarna är separata.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={runner.isRunning ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PlayArrowIcon />}
              onClick={() => void handleRunCategory('produkter')}
              disabled={runner.isRunning}
              sx={{ backgroundColor: '#072f53', '&:hover': { backgroundColor: '#0a3d6b' }, whiteSpace: 'nowrap' }}
            >
              {runner.isRunning ? 'Kör...' : 'Kör för Produkter'}
            </Button>
            <Button
              variant="contained"
              startIcon={runner.isRunning ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PlayArrowIcon />}
              onClick={() => void handleRunCategory('vin')}
              disabled={runner.isRunning}
              sx={{ backgroundColor: '#072f53', '&:hover': { backgroundColor: '#0a3d6b' }, whiteSpace: 'nowrap' }}
            >
              {runner.isRunning ? 'Kör...' : 'Kör för Vin'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Tidigare körningar</Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={() => void loadRuns()} disabled={loadingRuns}>
            Uppdatera
          </Button>
        </Box>

        {runsError && (
          <Alert severity="error" sx={{ mb: 2 }}>{runsError}</Alert>
        )}

        {loadingRuns ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : runs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Inga körningar finns ännu. Välj "Kör för Produkter" eller "Kör för Vin" ovan för att starta.
          </Typography>
        ) : (
          <List disablePadding>
            {runs.map((run, index) => {
              const allPassed = run.failedCount === 0 && run.errorCount === 0;
              return (
                <ListItemButton
                  key={run.runId}
                  onClick={() => void runner.openRun(run.runId)}
                  sx={{
                    px: 1.5,
                    py: 1.5,
                    borderRadius: 1,
                    mb: index < runs.length - 1 ? 0.5 : 0,
                    border: '1px solid',
                    borderColor: allPassed ? '#c8e6c9' : '#ffcdd2',
                    backgroundColor: allPassed ? '#fafff8' : '#fffafa',
                    '&:hover': { backgroundColor: allPassed ? '#f1f8e9' : '#fff5f5' },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        {allPassed
                          ? <CheckCircleIcon sx={{ fontSize: 20, color: '#2e7d32' }} />
                          : <CancelIcon sx={{ fontSize: 20, color: '#d32f2f' }} />
                        }
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {runScopeLabel(run)}
                        </Typography>
                        {statusChip(run)}
                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                          <Chip size="small" label={`${run.totalAssets} st`} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                          <Chip size="small" label={`${run.passCount} OK`} variant="outlined" sx={{ fontSize: '0.7rem', color: '#2e7d32', borderColor: '#c8e6c9' }} />
                          {run.failedCount > 0 && (
                            <Chip size="small" label={`${run.failedCount} fel`} variant="outlined" sx={{ fontSize: '0.7rem', color: '#d32f2f', borderColor: '#ffcdd2' }} />
                          )}
                          {run.errorCount > 0 && (
                            <Chip size="small" label={`${run.errorCount} varning`} variant="outlined" sx={{ fontSize: '0.7rem', color: '#ed6c02', borderColor: '#fff3e0' }} />
                          )}
                        </Stack>
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {new Date(run.startedAt).toLocaleString('sv-SE')} — pixelkrav: {run.settings.minRequiredSidePx} px
                      </Typography>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Paper>

      <ProductImageValidationRunDialog
        open={runner.dialogOpen}
        job={runner.job}
        run={runner.run}
        error={runner.error}
        starting={runner.starting}
        loadingRun={runner.loadingRun}
        onClose={runner.closeDialog}
        onDownload={(format) => void runner.downloadRun(format)}
      />
    </Box>
  );
};

export default ProductImageValidationPage;
